import { TablePaginationConfig } from 'antd';
import { useState, useEffect } from 'react';

export interface PaginationResponse<T> {
  total: number,
  limit: number,
  skip: number,
  data: T[],
}

export interface PaginationProps {
  skip: number,
  limit: number,
  params?: any,
}

// return service intefrace
export interface UsePaginationService<T> extends PaginationResponse<T> {
  // ...PaginationProps
  loading: boolean,
  pageSize: number,
  currentPage: number,
  setCurrentPage: Function,
  setCurrentPageSize: Function,
  pagination: TablePaginationConfig,
  setCurrentParams: any,
  currentParams: any,
  onPageChange: any,
  load: ()=> Promise<void>
  setPagination: React.Dispatch<React.SetStateAction<PaginationResponse<T>>>
}

// hook props
interface UsePaginationServiceProps<T> {
  currentPage?: number; // page skip
  pageSize?: number; // page skip
  params?: any; 
  forceParams?: boolean;
  fetchPage: (skip: number, limit: number, params?: any) => Promise<PaginationResponse<T>>;
}

function usePaginationService<T>({ 
  currentPage, 
  pageSize, 
  params,
  forceParams = false,
  fetchPage, 
}: UsePaginationServiceProps<T>): UsePaginationService<T> {
  const [pagination, setPagination] = useState<PaginationResponse<T>>({ 
    total: 0,
    limit: 0,
    skip: 0,
    data: [],
  });
  const [page, setCurrentPage] = useState<number>(currentPage || 1);
  const [currentPageSize, setCurrentPageSize] = useState(pageSize ?? 10);
  const [currentParams, setCurrentParams] = useState(params || {});
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    const recordsToSkip = (page - 1) * currentPageSize;

    fetchPage(recordsToSkip, currentPageSize, currentParams)
      .then((res) => {
        setPagination(res);
      })
      .catch((err) => {
        console.error(err);
        setPagination({
          total: 0,
          limit: 0,
          skip: 0,
          data: [],
        });
      })
      .finally(() => {
          setLoading(false);
      });
  }
  function handleChangePage(
    tableConfig: TablePaginationConfig, 
    // filters: Record<string, FilterValue | null>, 
    // sorter: SorterResult<RecordType> | SorterResult<RecordType>[], 
    // extra: TableCurrentDataSource<RecordType>,
  ) {
    // console.log('tableConfig', tableConfig);
    setCurrentPage(tableConfig.current || 2);
  }
  function handlePageSizeChange(current: number, size: number) {
    setCurrentPageSize(size);
  }

  useEffect(() => {
    const isSomeParamsNull = Object.values(currentParams).some(param => param === null);

    if (forceParams && isSomeParamsNull) return;
    load();    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, currentPageSize, currentParams]);
  
  return {
    ...pagination,
    data: pagination.data,
    loading,
    pageSize: currentPageSize,
    currentPage: page,
    setCurrentPage,
    setCurrentPageSize,
    currentParams,
    setCurrentParams,
    setPagination,
    pagination: {
      total: pagination.total,
      current: page,
      pageSize: currentPageSize,
      onShowSizeChange: handlePageSizeChange,
    },
    onPageChange: handleChangePage,
    load,
  };
}

export default usePaginationService;
